/**
 * useImageTransition — layered image swapping with preload cache and three
 * transition engines (crossfade, pixelBlock, slide).
 *
 * The caller passes the target URL plus the transition type; the hook
 * returns the visual state the card should render: `currentSrc` (the image
 * already on screen), `nextSrc` (the image transitioning in, or null when
 * idle), and `isFirstLoad` (so the card can show a shimmer on initial fetch
 * only). Blob-URL caching means a repeat request for the same URL is instant.
 *
 * Preload: after a successful swap the hook schedules a single-step lookahead
 * — it prefetches the caller-provided `lookaheadUrl`. This keeps auto-cycling
 * instant without burning bandwidth on all 8 options up front.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import type { TransitionType } from '../../config/transitions';

// Re-export so existing imports from this file keep working.
export type { TransitionType };
export { DEFAULT_TRANSITION } from '../../config/transitions';

interface UseImageTransitionArgs {
  targetUrl: string;
  transition: TransitionType;
  lookaheadUrl?: string | null;
  durationMs?: number;
}

interface UseImageTransitionResult {
  currentSrc: string | null;   // image fully visible right now
  nextSrc: string | null;      // image being transitioned in; null when idle
  isAnimating: boolean;
  isFirstLoad: boolean;
}

const DEFAULT_DURATION = 500;

/**
 * Fetches a URL and returns a blob URL suitable for <img src>. Resolves the
 * promise only after the image has decoded, so transitions never show a
 * half-rendered frame.
 */
async function fetchAsBlobUrl(url: string): Promise<string> {
  const response = await fetch(url, { credentials: 'same-origin' });
  if (!response.ok) throw new Error(`Visualizer fetch failed: ${response.status}`);
  const blob = await response.blob();
  const blobUrl = URL.createObjectURL(blob);

  // Decode so the blob is ready to paint.
  await new Promise<void>((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve();
    img.onerror = () => reject(new Error('Decode failed'));
    img.src = blobUrl;
  });

  return blobUrl;
}

export function useImageTransition({
  targetUrl,
  transition,
  lookaheadUrl,
  durationMs = DEFAULT_DURATION,
}: UseImageTransitionArgs): UseImageTransitionResult {
  const [currentSrc, setCurrentSrc] = useState<string | null>(null);
  const [nextSrc, setNextSrc] = useState<string | null>(null);
  const [isAnimating, setIsAnimating] = useState(false);
  const [isFirstLoad, setIsFirstLoad] = useState(true);

  // Ref mirror of currentSrc so the effect can read the latest value without
  // listing currentSrc as a dep (which would re-run the effect on every swap).
  const currentSrcRef = useRef<string | null>(null);

  // Cache: requested URL -> resolved blob URL. Survives re-renders.
  const cacheRef = useRef<Map<string, string>>(new Map());
  // In-flight fetches, so we don't duplicate a request when cycling fast.
  const inflightRef = useRef<Map<string, Promise<string>>>(new Map());
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // Cleanup all blob URLs on unmount.
  useEffect(() => {
    const cache = cacheRef.current;
    return () => {
      for (const url of cache.values()) URL.revokeObjectURL(url);
      cache.clear();
    };
  }, []);

  const resolveUrl = useCallback(async (url: string): Promise<string> => {
    const cached = cacheRef.current.get(url);
    if (cached) return cached;
    const existing = inflightRef.current.get(url);
    if (existing) return existing;

    const promise = fetchAsBlobUrl(url)
      .then(blobUrl => {
        cacheRef.current.set(url, blobUrl);
        inflightRef.current.delete(url);
        return blobUrl;
      })
      .catch(err => {
        inflightRef.current.delete(url);
        throw err;
      });
    inflightRef.current.set(url, promise);
    return promise;
  }, []);

  // Drive the actual swap whenever targetUrl or transition changes.
  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const resolved = await resolveUrl(targetUrl);
        if (cancelled || !mountedRef.current) return;

        // First load: no crossfade, just paint.
        if (currentSrcRef.current === null) {
          currentSrcRef.current = resolved;
          setCurrentSrc(resolved);
          setIsFirstLoad(false);
          return;
        }

        // Same image already showing: no-op.
        if (currentSrcRef.current === resolved) return;

        // Start animation.
        setNextSrc(resolved);
        setIsAnimating(true);

        // Commit after the transition window.
        window.setTimeout(() => {
          if (cancelled || !mountedRef.current) return;
          currentSrcRef.current = resolved;
          setCurrentSrc(resolved);
          setNextSrc(null);
          setIsAnimating(false);
        }, durationMs);
      } catch (err) {
        // Silent failure: leave the current image in place.
        // The PHP endpoint already redirects to offline.jpg on server errors.
        console.warn('[useImageTransition]', err);
      }
    })();

    return () => { cancelled = true; };
  }, [targetUrl, transition, resolveUrl, durationMs]);

  // Lookahead prefetch (1-ahead, fires after current load stabilizes).
  useEffect(() => {
    if (!lookaheadUrl || lookaheadUrl === targetUrl) return;
    if (cacheRef.current.has(lookaheadUrl)) return;
    const timer = window.setTimeout(() => {
      resolveUrl(lookaheadUrl).catch(() => { /* ignore */ });
    }, 500);
    return () => window.clearTimeout(timer);
  }, [lookaheadUrl, targetUrl, resolveUrl]);

  return { currentSrc, nextSrc, isAnimating, isFirstLoad };
}
